<template>
  <header class="site-header" @mouseleave="scheduleCloseMenu">
    <div class="topline">
      <button type="button" class="topline-link lang-toggle" @click="front.toggleLanguage">{{ front.languageLabel }}</button>
      <div v-if="front.isLoggedIn" class="notice-menu" @click.stop>
        <button type="button" class="topline-link notice-trigger" @click="onNoticeClick">
          通知
          <strong v-if="front.unreadNoticeCount">{{ front.unreadNoticeCount }}</strong>
        </button>
        <div
          v-if="front.notificationOpen"
          class="notice-popover"
        >
          <div v-if="front.unreadNoticeCount" class="notice-popover__head">
            <button
              type="button"
              class="notice-mark-all"
              @click="front.markVisibleNotificationsRead"
            >
              全部标为已读
            </button>
          </div>
          <div class="notice-popover__list">
            <button
              v-for="notice in front.systemNotifications"
              :key="notice.id"
              type="button"
              class="notice-item"
              :class="front.isNotificationRead(notice) ? 'notice-item--read' : 'notice-item--unread'"
              @click="front.openSystemNotification(notice)"
            >
              <span class="notice-item__meta">
                <i v-if="!front.isNotificationRead(notice)" class="notice-item__dot" aria-hidden="true"></i>
                <span>{{ front.displayNotificationType(notice) }}</span>
              </span>
              <strong>{{ notice.title }}</strong>
              <small>{{ notice.date }}</small>
            </button>
            <p v-if="!front.systemNotifications.length">暂无通知</p>
          </div>
        </div>
      </div>
      <div v-if="front.isLoggedIn" class="account-menu" @click.stop>
        <button type="button" class="account-trigger" @click="front.toggleAccountDropdown">
          <span class="account-avatar" aria-hidden="true"></span>
          <span>{{ front.userNickname }}</span>
          <i :class="{ open: front.accountDropdownOpen }"></i>
        </button>
        <div v-if="front.accountDropdownOpen" class="account-popover">
          <button type="button" @click="front.openAccountCenter">个人中心</button>
          <button type="button" @click="front.logout">安全退出</button>
        </div>
      </div>
      <button
        v-if="front.isLoggedIn && front.pwdWarn"
        type="button"
        class="topline-link pwd-warn"
        @click="front.$router.push({ path: '/personal-center', query: { section: 'security' } })"
      >
        口令将于 {{ front.pwdDaysLeft }} 天后过期
      </button>
      <button v-else-if="!front.isLoggedIn" type="button" class="topline-link" @click="front.openAuth('login')">登录</button>
      <button
        v-if="front.showJoinMemberCta"
        type="button"
        class="topline-member"
        @click="front.onJoinMemberClick"
      >
        加入会员
      </button>
      <button type="button" class="topline-link" @click="front.openContactUs">联系我们</button>
      <label class="topline-search">
        <input v-model="front.searchKeyword" type="search" placeholder="搜索..." @keyup.enter="front.submitSearch" />
        <button type="button" aria-label="搜索" @click="front.submitSearch">⌕</button>
      </label>
    </div>
    <div class="brand-shell">
      <button class="brand" type="button" aria-label="中国古生物学会首页" @click="front.goPath('/')">
        <span
          class="brand-logo"
          :style="{ '--brand-logo-image': `url(${societyLogo})` }"
          aria-hidden="true"
        ></span>
      </button>
    </div>
    <div class="nav-shell">
      <button type="button" class="mobile-nav-toggle" aria-label="打开导航" @click="front.mobileNavOpen = !front.mobileNavOpen">
        <span></span>
        <span></span>
        <span></span>
      </button>
      <nav class="desktop-nav" :class="{ open: front.mobileNavOpen }" aria-label="主导航">
        <div
          v-for="item in front.displayNavItems"
          :key="item.id || item.label"
          class="nav-item"
          :class="{
            'has-menu': hasMenu(item),
            open: openNavId === item.id,
            active: front.isNavActive(item)
          }"
          @mouseenter="openMenu(item)"
          @focusin="openMenu(item)"
        >
          <button
            type="button"
            class="nav-trigger"
            :class="{ active: front.isNavActive(item), open: openNavId === item.id }"
            :aria-expanded="hasMenu(item) ? openNavId === item.id : undefined"
            :aria-haspopup="hasMenu(item) ? 'true' : undefined"
            @click="onNavClick(item)"
          >
            {{ item.label }}
          </button>

          <div
            v-if="hasMenu(item) && front.mobileNavOpen && openNavId === item.id"
            class="nav-mobile-children"
          >
            <template v-if="menuFor(item).kind === 'mega' && menuFor(item).groups">
              <div
                v-for="group in menuFor(item).groups"
                :key="group.title"
                class="nav-mobile-group"
              >
                <strong>{{ group.title }}</strong>
                <button
                  v-for="child in group.children"
                  :key="child.id"
                  type="button"
                  @click="goChild(child)"
                >
                  {{ child.label }}
                </button>
              </div>
            </template>
            <button
              v-for="child in flatChildren(item)"
              v-show="!(menuFor(item).groups && menuFor(item).groups.length)"
              :key="'flat-' + child.id"
              type="button"
              @click="goChild(child)"
            >
              {{ child.label }}
            </button>
          </div>

          <div
            v-if="menuFor(item).kind === 'compact' && openNavId === item.id && !front.mobileNavOpen"
            class="nav-dropdown nav-dropdown--compact"
            @mouseenter="cancelCloseMenu"
          >
            <button
              v-for="child in flatChildren(item)"
              :key="child.id"
              type="button"
              @click="goChild(child)"
            >
              {{ child.label }}
            </button>
          </div>
        </div>
      </nav>
    </div>

    <div
      v-if="activeMegaMenu"
      class="nav-mega-wrap"
      @mouseenter="cancelCloseMenu"
      @mouseleave="scheduleCloseMenu"
    >
      <div class="nav-mega" :class="'nav-mega--' + openNavId">
        <div class="nav-mega__head">
          <h3>{{ activeMegaMenu.title }}</h3>
          <button type="button" class="nav-mega__enter" @click="enterOpenNav">进入栏目</button>
        </div>

        <div v-if="activeMegaMenu.rows" class="nav-mega__rows">
          <div
            v-for="row in activeMegaMenu.rows"
            :key="row.id"
            class="nav-mega__row"
            :class="{
              'nav-mega__row--primary': row.level === 'primary',
              'nav-mega__row--dense': row.children.length > 8
            }"
          >
            <div v-if="row.label" class="nav-mega__row-label">{{ row.label }}</div>
            <div class="nav-mega__row-grid" :style="rowGridStyle(row)">
              <button
                v-for="child in row.children"
                :key="child.id"
                type="button"
                @click="goChild(child)"
              >
                {{ child.label }}
              </button>
            </div>
          </div>
        </div>
        <div v-else-if="activeMegaMenu.groups" class="nav-mega__groups">
          <section v-for="group in activeMegaMenu.groups" :key="group.title" class="nav-mega__group">
            <h4>{{ group.title }}</h4>
            <div class="nav-mega__grid" :class="{ 'nav-mega__grid--dense': group.children.length > 6 }">
              <button
                v-for="child in group.children"
                :key="child.id"
                type="button"
                @click="goChild(child)"
              >
                {{ child.label }}
              </button>
            </div>
          </section>
        </div>
        <div v-else class="nav-mega__grid" :class="{ 'nav-mega__grid--dense': flatActiveChildren.length > 8 }">
          <button
            v-for="child in flatActiveChildren"
            :key="child.id"
            type="button"
            @click="goChild(child)"
          >
            {{ child.label }}
          </button>
        </div>
      </div>
    </div>
    <button
      v-if="front.isLoggedIn && front.pwdWarn"
      type="button"
      class="pwd-expire-banner"
      @click="front.$router.push({ path: '/personal-center', query: { section: 'security' } })"
    >
      口令将于 {{ front.pwdDaysLeft }} 天后过期，请尽快到个人中心修改
    </button>
  </header>
</template>

<script>
import { buildNavMenuMap } from '../../data/navMenus'
import { displayAssociationName, sortSocietiesMainFirst } from '../../data/branchSite'
import societyLogo from '../../assets/logo/society-logo-gold.png'

export default {
  name: 'SiteHeader',
  inject: ['front'],
  data() {
    return {
      societyLogo,
      openNavId: '',
      closeTimer: null,
      menuMap: buildNavMenuMap()
    }
  },
  computed: {
    structureChildren() {
      const list = sortSocietiesMainFirst(this.front.bindableAssociations || [])
      if (!list.length) {
        return [{ id: 'structure-home', label: '组织机构', path: '/structure' }]
      }
    return list.map(item => ({
      id: `society-${item.associationId}`,
      label: displayAssociationName(item),
      path: '/structure',
      query: { associationId: String(item.associationId) }
    }))
    },
    resolvedMenuMap() {
      const map = JSON.parse(JSON.stringify(this.menuMap))
      if (map.structure) {
        const children = this.structureChildren
        map.structure.rows = [
          { id: 'structure-main', label: '总学会', level: 'primary', children: children.slice(0, 1) },
          {
            id: 'structure-branches',
            label: `专业分会（${Math.max(children.length - 1, 0)}）`,
            children: children.slice(1)
          }
        ].filter(row => row.children.length)
        delete map.structure.children
        delete map.structure.groups
      }
      return map
    },
    activeMegaMenu() {
      if (!this.openNavId || this.front.mobileNavOpen) return null
      const menu = this.resolvedMenuMap[this.openNavId]
      return menu && menu.kind === 'mega' ? menu : null
    },
    flatActiveChildren() {
      return this.flatChildren({ id: this.openNavId })
    }
  },
  beforeDestroy() {
    this.cancelCloseMenu()
  },
  methods: {
    menuFor(item) {
      return this.resolvedMenuMap[item.id] || { kind: 'none', title: item.label, children: [] }
    },
    hasMenu(item) {
      const menu = this.menuFor(item)
      if (menu.kind === 'none') return false
      if (menu.rows) {
        return menu.rows.some(row => (row.children || []).length)
      }
      if (menu.groups) {
        return menu.groups.some(g => (g.children || []).length)
      }
      return (menu.children || []).length > 0
    },
    flatChildren(item) {
      const menu = this.menuFor(item)
      if (menu.rows) {
        return menu.rows.reduce((list, row) => list.concat(row.children || []), [])
      }
      if (menu.groups) {
        return menu.groups.reduce((list, g) => list.concat(g.children || []), [])
      }
      return menu.children || []
    },
    openMenu(item) {
      this.cancelCloseMenu()
      // 打开栏目时关闭通知/账号下拉，避免互相遮挡
      this.front.notificationOpen = false
      this.front.accountDropdownOpen = false
      if (!this.hasMenu(item)) {
        this.openNavId = ''
        return
      }
      this.openNavId = item.id
    },
    rowGridStyle(row) {
      const count = Math.max((row.children || []).length, 1)
      return { gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }
    },
    onNoticeClick() {
      // 先收起栏目 mega/dropdown，再打开通知，避免被 z-index 更高的栏目框挡住
      this.cancelCloseMenu()
      this.openNavId = ''
      this.front.accountDropdownOpen = false
      this.front.toggleNotificationMenu()
    },
    scheduleCloseMenu() {
      this.cancelCloseMenu()
      this.closeTimer = setTimeout(() => {
        this.openNavId = ''
        this.closeTimer = null
      }, 160)
    },
    cancelCloseMenu() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    },
    onNavClick(item) {
      if (this.front.mobileNavOpen && this.hasMenu(item)) {
        this.openNavId = this.openNavId === item.id ? '' : item.id
        return
      }
      this.openNavId = ''
      this.front.mobileNavOpen = false
      this.front.navigateNav(item)
    },
    enterOpenNav() {
      const item = this.front.displayNavItems.find(nav => nav.id === this.openNavId)
      if (item) this.onNavClick(item)
    },
    goChild(child) {
      this.openNavId = ''
      this.front.mobileNavOpen = false
      this.cancelCloseMenu()

      if (child.action === 'services-tab' || child.tab) {
        this.front.goServicesTab(child.tab || (child.query && child.query.tab))
        return
      }
      if (child.action === 'services-conference') {
        this.front.goServicesTab('conference')
        return
      }
      if (child.action === 'public-files') {
        this.front.goPath('/public-downloads')
        this.$nextTick(() => {
          this.front.selectPublicFileCategory(child.category || 'DOCUMENT')
        })
        return
      }
      if (child.action === 'party-downloads') {
        this.front.goPath(child.path || '/downloads')
        return
      }

      if (child.query && Object.keys(child.query).length) {
        this.$router.push({
          path: child.path || '/',
          query: child.query,
          hash: child.hash ? `#${child.hash}` : undefined
        })
        return
      }
      if (child.hash) {
        const base = child.path || '/intro'
        if (this.$route.path === base) {
          window.location.hash = child.hash
          return
        }
        this.front.goPath(`${base}#${child.hash}`)
        return
      }
      this.front.goPath(child.path || '/')
    }
  }
}
</script>
